import React, { useEffect, useState } from "react"
import { useNavigate, useParams } from 'react-router'
import { useTranslation } from 'react-i18next'
import { useSelector } from 'react-redux'
import { Modal } from 'react-bootstrap'
import Select from 'react-select'

import { handleDecimalKeyDown } from 'helpers/decimalKey'
import { httpOk, GET, POST } from "helpers/api"
import { formatMoney, formatDate, formatMoneyInput, notify } from 'helpers/helpers'

// Возврат от контрагента: сверяем, что пришло, и принимаем на склад или отклоняем.
// Пришло меньше — правим «Принято» по строкам: документ покупателя перепроведётся
// на принятое, непринятое вернётся к нему на склад.
function Preview() {
	const { t } = useTranslation()
	const { id } = useParams()
	const navigate = useNavigate()

	const reduxAccount = useSelector(state => state.account)

	const [data, setData] = useState({ 'items': [] })
	const [wms, setWms] = useState([])
	const [wmsId, setWmsId] = useState(null)
	const [modalAccept, setModalAccept] = useState(false)
	const [modalReject, setModalReject] = useState(false)
	const [rejectReason, setRejectReason] = useState('')

	const isPending = Number(data.status) === -2

	async function getData() {
		const response = await GET(`/service/returns-in/${id}`, {}, { loader: true })
		if (httpOk(response)) {
			const document = response.data
			document.items = (document.items ?? []).map(item => ({
				...item,
				'accepted_quantity': item.accepted_quantity ?? item.quantity,
			}))
			setData(document)
			// По умолчанию — склад, с которого товар уезжал
			setWmsId(document.target_wms_id ?? document.from_wms_id ?? reduxAccount.wms_id)
		}
	}

	async function getWms() {
		const response = await GET(`/helper/wms?pos_id=${reduxAccount.pos_id}`, {}, { loader: true })
		if (httpOk(response)) setWms(response.data)
	}

	function handleAcceptedChange(index, value) {
		const items = [...data.items]
		items[index] = { ...items[index], 'accepted_quantity': value.replace(/,/g, '.').replace(/[^0-9.]/g, '') }
		setData({ ...data, 'items': items })
	}

	function isLineChanged(item) {
		return Math.abs(Number(item.accepted_quantity) - Number(item.quantity)) > 0.0001
	}

	function lineAcceptedAmount(item) {
		if (Number(item.quantity) <= 0) return 0
		return Number(item.amount) * Number(item.accepted_quantity || 0) / Number(item.quantity)
	}

	function openAccept() {
		if (!wmsId) {
			notify(t('select_wms'))
			return
		}
		if (data.items.some(item => Number(item.accepted_quantity) > Number(item.quantity) + 0.0001)) {
			notify(t('accepted_exceeds_sent'))
			return
		}
		if (!data.items.some(item => Number(item.accepted_quantity) > 0)) {
			notify(t('nothing_accepted_reject_instead'))
			return
		}
		setModalAccept(true)
	}

	async function accept() {
		const response = await POST(`/service/returns-in/${id}/accept`, {
			'wms_id': wmsId,
			'items': data.items.map(item => ({
				'id': item.id,
				'accepted_quantity': Number(item.accepted_quantity || 0),
			})),
		}, { loader: true })

		if (httpOk(response)) {
			setModalAccept(false)
			navigate('/service/returns-in')
		}
	}

	async function reject() {
		const response = await POST(`/service/returns-in/${id}/reject`, { 'reason': rejectReason }, { loader: true })
		if (httpOk(response)) {
			setModalReject(false)
			navigate('/service/returns-in')
		}
	}

	useEffect(() => {
		getWms()
		getData()
	}, []) // eslint-disable-line react-hooks/exhaustive-deps

	const totalSent = data.items.reduce((sum, item) => sum + Number(item.amount), 0)
	const totalAccepted = data.items.reduce((sum, item) => sum + lineAcceptedAmount(item), 0)

	return (
		<>
			<div className="page-header d-flex justify-content-between">
				<div className="d-flex text-truncate">
					<h4 className="vertical-center">
						{t('returns_from_counterparty')}
					</h4>
					<div className="vertical-center mt-1">
						<i className="uil uil-angle-double-right fz-20"></i>
					</div>
					<div className="vertical-center mt-1">
						<h6>№{data.id}</h6>
					</div>
				</div>
			</div>

			<div className="card mb-3">
				<div className="card-body">
					<h4 className="fw-600">{t('document_from')} {formatDate(data.created_at, 'dd.MM.yy HH:mm')}</h4>
					<div className="flex-column preview-info">
						<div className="d-flex gap-2">
							<div className="fw-600">{t('counterparty')}:</div>
							<div>{data.source_pos_name}</div>
						</div>
						<div className="d-flex gap-2">
							<div className="fw-600">{t('return_basis_transfer')}:</div>
							<div>№{data.wms_transfer_pos_id}</div>
						</div>
						<div className="d-flex gap-2">
							<div className="fw-600">{t('note')}:</div>
							<div>{data.note}</div>
						</div>
						{Number(data.status) === 1 &&
							<div className="d-flex gap-2">
								<div className="fw-600">{t('integration_return_status_accepted')}:</div>
								<div>{formatDate(data.accepted_at, 'dd.MM.yy HH:mm')} · {data.wms_name}</div>
							</div>
						}
						{Number(data.status) === -1 &&
							<div className="d-flex gap-2 text-danger">
								<div className="fw-600">{t('integration_return_status_rejected')}:</div>
								<div>{formatDate(data.rejected_at, 'dd.MM.yy HH:mm')} · {data.reject_reason}</div>
							</div>
						}
					</div>

					{isPending &&
						<div className="row mt-3">
							<div className="col-md-3">
								<div className="form-group mb-0">
									<label>{t('accept_to_wms')}<span className="required-mark">*</span></label>
									<Select
										options={wms}
										value={wms.find(option => option.id === wmsId) || null}
										onChange={(option) => setWmsId(option.id)}
										placeholder=""
										noOptionsMessage={() => t('list_empty')}
										getOptionLabel={(option) => option.name}
										getOptionValue={(option) => option.id}
									/>
								</div>
							</div>
							<div className="col-md-9 vertical-center">
								<small className="text-muted">{t('integration_return_accept_hint')}</small>
							</div>
						</div>
					}
				</div>
			</div>

			<div className="card">
				<div className="card-body">
					<div className="table-responsive">
						<table className="table table-bordered">
							<thead>
								<tr>
									<th style={{ 'minWidth': '33px' }} className="text-center">#</th>
									<th style={{ 'width': '40%' }}>{t('name2')}</th>
									<th style={{ 'width': '10%' }}>{t('unit')}</th>
									<th style={{ 'width': '12%' }} className="text-end">{t('sent')}</th>
									<th style={{ 'width': '12%' }} className="text-end">{t('accepted')}</th>
									<th style={{ 'width': '12%' }} className="text-end">{t('price')}</th>
									<th style={{ 'width': '14%' }} className="text-end">{t('amount')}</th>
								</tr>
							</thead>
							<tbody>
								{data.items.map((item, index) => (
									<tr key={item.id} className={isLineChanged(item) ? 'table-warning' : ''}>
										<td className="text-center">{index + 1}</td>
										<td>{item.product_name}</td>
										<td>{item.unit_name}</td>
										<td className="text-end">{formatMoney(item.quantity, 3)}</td>
										{isPending ?
											<td className="td-input"
												style={Number(item.accepted_quantity) > Number(item.quantity) + 0.0001 ? { 'borderBottom': '2px solid red' } : {}}>
												<input
													type="text"
													className="form-control text-end"
													placeholder="0"
													value={formatMoneyInput(item.accepted_quantity) ?? ''}
													onChange={(e) => handleAcceptedChange(index, e.target.value)}
													onKeyDown={(e) => handleDecimalKeyDown(e, (event) => handleAcceptedChange(index, event.target.value))} />
											</td>
											:
											<td className="text-end">{formatMoney(item.accepted_quantity, 3)}</td>
										}
										<td className="text-end">{formatMoney(item.price)}</td>
										<td className="text-end">{formatMoney(isPending ? lineAcceptedAmount(item) : item.amount, 2)}</td>
									</tr>
								))}
								<tr>
									<td></td>
									<td><b>{t('total')}</b></td>
									<td colSpan={4}></td>
									<td className="text-end text-nowrap">
										<b>{formatMoney(isPending ? totalAccepted : data.total_amount, 2)}</b>
										{isPending && Math.abs(totalAccepted - totalSent) > 0.01 &&
											<div className="text-muted"><small>{t('sent')}: {formatMoney(totalSent, 2)}</small></div>
										}
									</td>
								</tr>
							</tbody>
						</table>
					</div>

					<div className="d-flex justify-content-end gap-2 mt-5">
						<button type="button" className="btn btn-warning btn-rounded btn-wide"
							onClick={() => navigate(-1)}>
							{t('cancel')}
						</button>
						{isPending &&
							<>
								<button type="button" className="btn btn-danger btn-rounded btn-wide"
									onClick={() => { setRejectReason(''); setModalReject(true) }}>
									{t('reject')}
								</button>
								<button type="button" className="btn btn-primary btn-rounded btn-wide"
									onClick={() => openAccept()}>
									{t('accept')}
								</button>
							</>
						}
					</div>
				</div>
			</div>

			{/* MODAL ACCEPT */}
			<Modal show={modalAccept} animation={false} centered
				dialogClassName="update-modal-width" onHide={() => setModalAccept(false)}>
				<Modal.Header closeButton>
					<Modal.Title>{t('accept')}</Modal.Title>
				</Modal.Header>
				<Modal.Body>
					<p className="mb-2">
						{t('accept_to_wms')}: <b>{wms.find(option => option.id === wmsId)?.name}</b>
					</p>
					<p className="mb-3">
						{t('amount')}: <b>{formatMoney(totalAccepted, 2)}</b>
					</p>
					{data.items.some(item => isLineChanged(item)) &&
						<div className="alert alert-warning">{t('integration_return_partial_warning')}</div>
					}
					<div className="d-flex gap-2 w-100">
						<button className="btn btn-warning w-100" onClick={() => setModalAccept(false)}>
							{t('cancel')}
						</button>
						<button className="btn btn-primary w-100" onClick={() => accept()}>
							{t('confirm')}
						</button>
					</div>
				</Modal.Body>
			</Modal>
			{/* MODAL ACCEPT */}

			{/* MODAL REJECT */}
			<Modal show={modalReject} animation={false} centered backdrop="static"
				dialogClassName="update-modal-width" onHide={() => setModalReject(false)}>
				<Modal.Header closeButton>
					<Modal.Title>{t('reject')}</Modal.Title>
				</Modal.Header>
				<Modal.Body>
					<div className="form-group">
						<label>{t('reason')}<span className="required-mark">*</span></label>
						<input type="text" className="form-control"
							autoFocus
							value={rejectReason}
							onChange={(e) => setRejectReason(e.target.value)} />
					</div>
					<p className="text-muted"><small>{t('integration_return_reject_hint')}</small></p>
					<div className="d-flex gap-2 w-100">
						<button className="btn btn-warning w-100" onClick={() => setModalReject(false)}>
							{t('cancel')}
						</button>
						<button className="btn btn-danger w-100"
							disabled={!rejectReason.trim()}
							onClick={() => reject()}>
							{t('reject')}
						</button>
					</div>
				</Modal.Body>
			</Modal>
			{/* MODAL REJECT */}
		</>
	)
}

export default Preview
